/**
 * SelectionSummary.js
 * Enhanced component for displaying reconciliation selection summary with transaction preview
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  useTheme,
  Collapse,
  Divider,
  Avatar,
  alpha,
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  Receipt as ReceiptIcon,
  ArrowForward as ArrowForwardIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { formatCurrency } from '../../helpers/formatCurrency';

const SelectionSummary = ({
  selectedStatement,
  selectedTransactions,
  selectedJournalEntries,
  canReconcile,
  selectionDifference,
  selectedTransactionTotal,
  selectedJournalEntryTotal,
  onClearSelections,
}) => {
  const theme = useTheme();
  const [expanded] = useState(false);

  const hasSelections = selectedTransactions.length > 0 || selectedJournalEntries.length > 0;

  return (
    <Paper
      elevation={2}
      sx={{
        flexShrink: 0,
        mb: 1,
        borderRadius: 2,
        overflow: 'hidden',
        background: hasSelections
          ? canReconcile
            ? 'linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%)'
            : 'linear-gradient(135deg, #fff3e0 0%, #fce4ec 100%)'
          : 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
        border: `1px solid ${
          hasSelections
            ? canReconcile
              ? theme.palette.success.main
              : theme.palette.warning.main
            : alpha(theme.palette.grey[300], 0.5)
        }`,
        transition: 'all 0.3s ease-in-out',
      }}
    >
              <Box sx={{ p: 1.5 }}>
                {hasSelections ? (
                  <Stack spacing={1.5}>
                    {/* Summary Cards */}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch">
                      {/* Lançamentos Card */}
                      <Paper
                        elevation={0}
                        sx={{
                          flex: 1,
                          p: 1,
                          borderRadius: 2,
                          backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                          border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                        }}
                      >
                        <Stack alignItems="center" spacing={1}>
                          <Avatar
                            sx={{
                              width: 28,
                              height: 28,
                              backgroundColor: theme.palette.secondary.main,
                              color: 'white',
                            }}
                          >
                            <ReceiptIcon fontSize="small" />
                          </Avatar>
                          <Typography variant="caption" fontWeight="600" color="text.secondary">
                            Lançamentos
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="700"
                            color={theme.palette.secondary.main}
                            sx={{ textAlign: 'center', fontSize: 22 }}
                          >
                            {formatCurrency(selectedJournalEntryTotal || 0)}
                          </Typography>
                        </Stack>
                      </Paper>
                      {/* Diferença Card (middle) */}
                      <Paper
                        elevation={0}
                        sx={{
                          flex: 1,
                          p: 1,
                          borderRadius: 2,
                          backgroundColor: canReconcile
                            ? alpha(theme.palette.success.main, 0.08)
                            : alpha(theme.palette.warning.main, 0.08),
                          border: `1px solid ${
                            canReconcile
                              ? alpha(theme.palette.success.main, 0.3)
                              : alpha(theme.palette.warning.main, 0.3)
                          }`,
                        }}
                      >
                        <Stack alignItems="center" spacing={1}>
                          <Avatar
                            sx={{
                              width: 28,
                              height: 28,
                              backgroundColor: canReconcile
                                ? theme.palette.success.main
                                : theme.palette.warning.main,
                              color: 'white',
                            }}
                          >
                            <ArrowForwardIcon fontSize="small" />
                          </Avatar>
                          <Typography variant="caption" fontWeight="600" color="text.secondary">
                            Diferença
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="700"
                            color={canReconcile ? theme.palette.success.main : theme.palette.warning.main}
                            sx={{ textAlign: 'center', fontSize: 22 }}
                          >
                            {formatCurrency(Math.abs(selectionDifference || 0))}
                          </Typography>
                        </Stack>
                      </Paper>
                      {/* Extratos Card */}
                      <Paper
                        elevation={0}
                        sx={{
                          flex: 1,
                          p: 1,
                          borderRadius: 2,
                          backgroundColor: alpha(theme.palette.info.main, 0.08),
                          border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                        }}
                      >
                        <Stack alignItems="center" spacing={1}>
                          <Avatar
                            sx={{
                              width: 28,
                              height: 28,
                              backgroundColor: theme.palette.info.main,
                              color: 'white',
                            }}
                          >
                            <WalletIcon fontSize="small" />
                          </Avatar>
                          <Typography variant="caption" fontWeight="600" color="text.secondary">
                            Extratos
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="700"
                            color={theme.palette.info.main}
                            sx={{ textAlign: 'center', fontSize: 22 }}
                          >
                            {formatCurrency(selectedTransactionTotal || 0)}
                          </Typography>
                        </Stack>
                      </Paper>
                    </Stack>
                    {/* Expandable Transaction Details */}
                    <Collapse in={expanded}>
                      <Divider sx={{ my: 1.5, borderColor: alpha(theme.palette.divider, 0.5) }} />
                      <Typography
                        variant="h6"
                        fontWeight="600"
                        sx={{ mb: 1.5, color: theme.palette.text.primary }}
                      >
                        Detalhes das Seleções
                      </Typography>
                      <Stack spacing={1}>
                        {/* Selected Transactions */}
                        {selectedTransactions.length > 0 && (
                          <Box>
                            <Typography
                              variant="body2"
                              fontWeight="600"
                              color="text.secondary"
                              sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
                            >
                              <WalletIcon fontSize="small" />
                              Extratos Bancários ({selectedTransactions.length})
                            </Typography>
                            <Stack spacing={0.5}>
                              {selectedTransactions.slice(0, 3).map((transaction, index) => (
                                <Paper
                                  key={transaction.id || index}
                                  elevation={0}
                                  sx={{
                                    p: 1,
                                    backgroundColor: alpha(theme.palette.info.main, 0.08),
                                    borderRadius: 1.5,
                                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                      backgroundColor: alpha(theme.palette.info.main, 0.12),
                                    },
                                  }}
                                >
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" noWrap sx={{ flex: 1, mr: 1 }}>
                                      {transaction.description || 'Sem descrição'}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      fontWeight="600"
                                      color={theme.palette.info.main}
                                    >
                                      {formatCurrency(transaction.amount || 0)}
                                    </Typography>
                                  </Stack>
                                </Paper>
                              ))}
                              {selectedTransactions.length > 3 && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ textAlign: 'center', py: 0.5, fontStyle: 'italic' }}
                                >
                                  +{selectedTransactions.length - 3} mais...
                                </Typography>
                              )}
                            </Stack>
                          </Box>
                        )}
                        {/* Selected Journal Entries */}
                        {selectedJournalEntries.length > 0 && (
                          <Box>
                            <Typography
                              variant="body2"
                              fontWeight="600"
                              color="text.secondary"
                              sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
                            >
                              <ReceiptIcon fontSize="small" />
                              Lançamentos Contábeis ({selectedJournalEntries.length})
                            </Typography>
                            <Stack spacing={0.5}>
                              {selectedJournalEntries.slice(0, 3).map((entry, index) => (
                                <Paper
                                  key={entry.id || index}
                                  elevation={0}
                                  sx={{
                                    p: 1,
                                    backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                                    borderRadius: 1.5,
                                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                      backgroundColor: alpha(theme.palette.secondary.main, 0.12),
                                    },
                                  }}
                                >
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" noWrap sx={{ flex: 1, mr: 1 }}>
                                      {entry.description || 'Sem descrição'}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      fontWeight="600"
                                      color={theme.palette.secondary.main}
                                    >
                                      {formatCurrency(entry.valueFinal ?? entry.value ?? 0)}
                                    </Typography>
                                  </Stack>
                                </Paper>
                              ))}
                              {selectedJournalEntries.length > 3 && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ textAlign: 'center', py: 0.5, fontStyle: 'italic' }}
                                >
                                  +{selectedJournalEntries.length - 3} mais...
                                </Typography>
                              )}
                            </Stack>
                          </Box>
                        )}
                      </Stack>
                    </Collapse>
                  </Stack>
                ) : (
                  /* Empty State */
                  <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center" sx={{ minHeight: 44 }}>
                    <InfoIcon sx={{ color: 'text.secondary', fontSize: '1.1rem' }} />
                    <Typography variant="body2" color="text.secondary" fontWeight="500">
                      Selecione itens nas tabelas para conciliar
                    </Typography>
                  </Stack>
                )}
              </Box>
            </Paper>
  );
};

export default SelectionSummary;
